import { ErrorBoundary } from '@sentry/react';
import { useRef } from 'react';
import { useOnClickOutside } from '@alchemer/ui/hooks/use-click-outside';
import { useIframeBlur } from '@/hooks/useIframeBlur';
import { useToggleState } from '@/hooks/useToggleState';
import UserMenuDialog, { UserMenuDialogType } from '../UserMenuDialog/UserMenuDialog';
import { tw } from '@/lib/tailwind';

export default function UserMenuItem({
  id,
  ariaLabel,
  icon,
  dialogType,
}: {
  id: string;
  ariaLabel: string;
  icon: React.ReactNode;
  dialogType: UserMenuDialogType;
}) {
  const htmlRef = useRef<HTMLDialogElement | null>(null);
  const userIconRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, toggle] = useToggleState(false);

  useOnClickOutside(htmlRef as React.RefObject<HTMLElement>, (event) => {
    if (!isOpen) return;
    // Clicking the icon button should NOT close the dialog
    if (userIconRef.current?.contains(event.target as Node)) return;

    toggle();
  });

  useIframeBlur(isOpen, toggle);

  return (
    <ErrorBoundary>
      <div className={tw('relative m-0 h-7 w-7 rounded-full p-0', isOpen ? 'border-border border-2 border-solid' : '')}>
        <button
          id={id}
          ref={userIconRef}
          aria-haspopup="dialog"
          aria-label={ariaLabel}
          className={tw('m-0 flex cursor-pointer border-0 bg-none p-0 outline-0')}
          onClick={toggle}
        >
          {icon}
        </button>

        <UserMenuDialog
          htmlRef={htmlRef}
          isOpen={isOpen}
          onClose={toggle}
          type={dialogType}
        />
      </div>
    </ErrorBoundary>
  );
}
